import { useState, useEffect, useRef } from 'react';
import { Search, Heart, ShoppingCart, User, Menu, X, Phone, Mail, Instagram, Facebook, Youtube, Truck, Tag, Package, MapPin, LogOut, ChevronRight, UserCircle2 } from 'lucide-react';
import { ICON_OPTIONS } from '../utils/iconOptions';

// ── Small inline SVGs for platforms not in lucide ─────────────────────────────
const TikTokIcon = () => (
  <svg className="w-3 h-3 fill-current" viewBox="0 0 448 512">
    <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
  </svg>
);
const PinterestIcon = () => (
  <svg className="w-3 h-3 fill-current" viewBox="0 0 496 512">
    <path d="M496 256c0 137-111 248-248 248-25.6 0-50.2-3.9-73.4-11.1 10.1-16.5 25.2-43.5 30.8-65 3-11.6 15.4-59 15.4-59 8.1 15.4 31.7 28.5 56.8 28.5 74.8 0 128.7-68.8 128.7-154.3 0-81.9-66.9-143.2-152.9-143.2-107 0-163.9 71.8-163.9 150.1 0 36.4 19.4 81.7 50.3 96.1 4.7 2.2 7.2 1.2 8.3-3.3.8-3.4 5-20.3 6.9-28.1.6-2.5.3-4.7-1.7-7.1-10.1-12.5-18.3-35.3-18.3-56.6 0-54.7 41.4-107.6 112-107.6 60.9 0 103.6 41.5 103.6 100.9 0 67.1-33.9 113.6-78 113.6-24.3 0-42.6-20.1-36.7-44.8 7-29.5 20.5-61.3 20.5-82.6 0-19-10.2-34.9-31.4-34.9-24.9 0-44.9 25.7-44.9 60.2 0 22 7.4 36.8 7.4 36.8s-24.5 103.8-29 123.2c-5 21.4-3 51.6-.9 71.2C65.4 450.9 0 361.1 0 256 0 119 111 8 248 8s248 111 248 248z"/>
  </svg>
);

const SOCIAL_ICONS = {
  instagram: <Instagram className="w-3 h-3" />,
  facebook:  <Facebook  className="w-3 h-3" />,
  youtube:   <Youtube   className="w-3 h-3" />,
  tiktok:    <TikTokIcon />,
  pinterest: <PinterestIcon />,
};

// ── Top Bar element renderer (white-on-gradient context) ─────────────────────
function renderTopBarEl(el) {
  const { type, props = {} } = el;
  switch (type) {
    case 'contact': {
      const isPhone = props.icon === 'phone';
      const href = isPhone
        ? `tel:${(props.value || '').replace(/[\s()-]/g, '')}`
        : `mailto:${props.value || ''}`;
      return (
        <a href={href} className="flex items-center gap-2 text-white/90 hover:text-white text-xs font-bold transition-colors" style={props.color ? { color: props.color } : {}}>
          {isPhone
            ? <Phone className="w-3 h-3" style={{ color: props.iconColor || '#FDE047' }} />
            : <Mail className="w-3 h-3" style={{ color: props.iconColor || '#FDE047' }} />}
          {props.value}
        </a>
      );
    }
    case 'text':
      return <span className="text-xs font-bold text-white/80" style={props.color ? { color: props.color } : {}} dangerouslySetInnerHTML={{ __html: props.html || props.text || '' }} />;
    case 'social':
      return (
        <div className="flex items-center gap-1">
          {(props.links || []).map(s => (
            <a key={s.id} href={s.url || '#'} target="_blank" rel="noreferrer"
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors text-white/90 hover:text-white">
              {SOCIAL_ICONS[s.platform] ?? <span className="text-xs font-bold">{(s.platform || '?')[0].toUpperCase()}</span>}
            </a>
          ))}
        </div>
      );
    case 'email': {
      const href = `mailto:${props.value || ''}`;
      return (
        <a href={href} className="flex items-center gap-2 text-white/90 hover:text-white text-xs font-bold transition-colors" style={props.color ? { color: props.color } : {}}>
          <Mail className="w-3 h-3" style={{ color: props.iconColor || '#FDE047' }} />
          {props.label || props.value}
        </a>
      );
    }
    case 'divider':
      return (
        <div style={{
          width:           props.thickness || '1px',
          height:          props.height    || '16px',
          backgroundColor: props.color     || 'rgba(255,255,255,0.3)',
          marginLeft:      props.margin    || '4px',
          marginRight:     props.margin    || '4px',
          flexShrink: 0,
        }} />
      );
    case 'spacer':
      return props.flex ? <div className="flex-1" /> : <div style={{ width: props.width || '16px', flexShrink: 0 }} />;
    case 'html':
      return <div dangerouslySetInnerHTML={{ __html: props.html || '' }} className="text-xs" />;
    case 'icon': {
      const Ic = ICON_OPTIONS[props.name] || ICON_OPTIONS.Star;
      const sz = props.size || '20px';
      const inner = (
        <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center',
          padding: props.bgColor ? (props.padding || '6px') : '0',
          backgroundColor: props.bgColor || undefined,
          borderRadius: props.bgColor ? (props.bgRounded ? '9999px' : '6px') : undefined,
          flexShrink: 0 }}>
          <Ic style={{ color: props.color || '#FDE047', width: sz, height: sz }} />
        </div>
      );
      return props.url ? <a href={props.url} target="_blank" rel="noreferrer">{inner}</a> : inner;
    }
    default:
      return null;
  }
}

// ── Top Bar component ─────────────────────────────────────────────────────────
function TopBar({ settings }) {
  // Detect new admin model (rows array) or migrated old left/right
  const hasNewModel = Array.isArray(settings?.rows) || Array.isArray(settings?.left) || Array.isArray(settings?.right);

  if (hasNewModel) {
    const rows     = settings.rows || [];
    const topStyle = settings.style ?? {};

    // Resolve background: gradient, solid, or default Tailwind gradient
    let bgCls    = '';
    let bgInline = {};
    if (topStyle.gradient && topStyle.gradientFrom) {
      const via = topStyle.gradientVia ? `, ${topStyle.gradientVia}` : '';
      bgInline = { background: `linear-gradient(to right, ${topStyle.gradientFrom}${via}, ${topStyle.gradientTo || topStyle.gradientFrom})` };
    } else if (topStyle.bgColor) {
      bgInline = { backgroundColor: topStyle.bgColor };
    } else {
      bgCls = 'bg-gradient-to-r from-primary via-primaryHover to-accent1';
    }

    return (
      <div className={`text-white ${bgCls}`} style={bgInline}>
        {rows.map((row, ri) => (
          <div key={row.id ?? ri} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-10 gap-4">
              {(row.columns || []).map((col) => (
                <div
                  key={col.id}
                  className="flex items-center gap-3"
                  style={{
                    flex: col.flex ?? 1,
                    justifyContent: col.align === 'end' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
                  }}
                >
                  {col.elements.map(el => <span key={el.id}>{renderTopBarEl(el)}</span>)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Legacy static model (DEFAULT_HEADER_MODERN.topBar)
  const {
    email        = 'hello@kiddiejoy.com',
    phone        = '+1 (800) 555-1234',
    promoText    = 'Free shipping on orders over $50!',
    shippingText = 'Free shipping over $50',
    socials      = [
      { id: 's1', platform: 'instagram', url: '#' },
      { id: 's2', platform: 'facebook',  url: '#' },
      { id: 's3', platform: 'tiktok',    url: '#' },
      { id: 's4', platform: 'youtube',   url: '#' },
      { id: 's5', platform: 'pinterest', url: '#' },
    ],
  } = settings ?? {};

  return (
    <div className="bg-gradient-to-r from-primary via-primaryHover to-accent1 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-10">

          {/* Left — email & phone (desktop only) */}
          <div className="hidden md:flex items-center gap-6">
            {email && (
              <a href={`mailto:${email}`} className="flex items-center gap-2 text-white/90 hover:text-white text-xs font-bold transition-colors">
                <Mail className="w-3 h-3 text-yellow-300" />
                {email}
              </a>
            )}
            {email && phone && <span className="w-px h-4 bg-white/30" />}
            {phone && (
              <a href={`tel:${phone.replace(/\s/g,'')}`} className="flex items-center gap-2 text-white/90 hover:text-white text-xs font-bold transition-colors">
                <Phone className="w-3 h-3 text-yellow-300" />
                {phone}
              </a>
            )}
          </div>

          {/* Center — promo text (mobile only) */}
          {promoText && (
            <div className="md:hidden flex items-center gap-2">
              <Tag className="w-3 h-3 text-yellow-300 flex-shrink-0" />
              <span className="text-xs font-black">{promoText}</span>
            </div>
          )}

          {/* Right — socials + shipping */}
          <div className="flex items-center gap-1">
            <span className="hidden md:inline text-xs text-white/70 font-bold mr-2">Follow us:</span>

            {socials.map(s => (
              <a key={s.id} href={s.url || '#'} target="_blank" rel="noreferrer"
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors text-white/90 hover:text-white">
                {SOCIAL_ICONS[s.platform] ?? <span className="text-xs font-bold">{s.platform[0].toUpperCase()}</span>}
              </a>
            ))}

            {shippingText && (
              <>
                <span className="hidden md:flex w-px h-4 bg-white/30 mx-1" />
                <span className="hidden md:flex items-center gap-1 text-xs text-white/90 font-bold ml-1">
                  <Truck className="w-3 h-3 text-yellow-300" />
                  {shippingText}
                </span>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
import SearchModal from '../components/SearchModal/SearchModal';

// ── User account dropdown (desktop hover, mobile inline) ──────────────────────
const ACCOUNT_LINKS = [
  { label: 'My Orders',  path: 'orders/',        icon: Package   },
  { label: 'Addresses',  path: 'edit-address/',   icon: MapPin    },
  { label: 'Wishlist',   path: 'wishlist/',       icon: Heart     },
  { label: 'Profile',    path: 'edit-account/',   icon: UserCircle2 },
];

function UserMenu() {
  const isLoggedIn   = !!window.themeData?.isLoggedIn;
  const displayName  = window.themeData?.userDisplayName ?? '';
  const userEmail    = window.themeData?.userEmail ?? '';
  const siteUrl      = window.themeData?.siteUrl ?? '';
  const myAccountUrl = window.themeData?.myAccountUrl ?? `${siteUrl}/my-account/`;
  const logoutUrl    = window.themeData?.logoutUrl ?? `${myAccountUrl}?action=logout`;
  const initial      = displayName?.[0]?.toUpperCase() ?? 'U';
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Guest: simple login link
  if (!isLoggedIn) {
    return (
      <a
        href={myAccountUrl}
        className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 hover:border-primary hover:bg-primary/5 transition-all text-sm font-black text-textDark group"
      >
        <User className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
        <span className="group-hover:text-primary transition-colors">Login</span>
      </a>
    );
  }

  // Logged-in: avatar + hover dropdown
  return (
    <div
      ref={ref}
      className="hidden lg:block relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-gray-50 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary font-black text-sm flex-shrink-0">
          {initial}
        </div>
        <span className="text-sm font-black text-textDark hidden xl:block max-w-[100px] truncate">{displayName || 'Account'}</span>
        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 hidden xl:block transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full pt-2 w-56 z-[60]">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* User info */}
            <div className="px-4 py-3.5 bg-gradient-to-r from-primary/5 to-accent1/5 border-b border-gray-100">
              <p className="font-black text-textDark text-sm leading-tight">{displayName}</p>
              {userEmail && <p className="text-xs text-gray-400 mt-0.5 truncate">{userEmail}</p>}
            </div>

            {/* Nav links */}
            <div className="py-1.5">
              {ACCOUNT_LINKS.map(({ label, path, icon: Icon }) => (
                <a
                  key={path}
                  href={`${myAccountUrl.replace(/\/$/, '')}/${path}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-primary/5 text-sm font-bold text-textDark hover:text-primary transition-colors group"
                >
                  <Icon className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors flex-shrink-0" />
                  {label}
                </a>
              ))}
            </div>

            {/* Logout */}
            <div className="border-t border-gray-100 py-1.5">
              <a
                href={logoutUrl}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 text-sm font-bold text-red-500 transition-colors group"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                Log Out
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared element renderer ───────────────────────────────────────────────────

const SOCIAL_ABBR  = { facebook:'FB', instagram:'IG', twitter:'X', youtube:'YT', tiktok:'TT', pinterest:'PT', whatsapp:'WA', linkedin:'LI' };
const SOCIAL_COLOR = { facebook:'#1877F2', instagram:'#E1306C', twitter:'#000', youtube:'#FF0000', tiktok:'#010101', pinterest:'#E60023', whatsapp:'#25D366', linkedin:'#0A66C2' };

export function renderElement(el, { cartCount = 0, wishlistCount = 0, siteUrl = '/', openSearch } = {}) {
  const { type, props = {} } = el;
  switch (type) {
    case 'logo':
      return (
        <a href={props.linkUrl || siteUrl} className="flex-shrink-0">
          {props.logoType === 'image' && props.logoUrl
            ? <img src={props.logoUrl} alt={props.logoText || 'Logo'} className="h-20 w-auto object-contain" />
            : <span className="text-2xl font-black bg-gradient-to-r from-primary to-accent1 bg-clip-text text-transparent">{props.logoText || 'KiddieJoy'}</span>
          }
        </a>
      );
    case 'menu': {
      const navMenuItems = window.themeData?.navMenuItems ?? {};
      const menuLinks = (props.menuId && navMenuItems[props.menuId])
        ? navMenuItems[props.menuId].filter(i => !i.parent)
        : (props.links ?? []);
      const color = props.color || undefined;
      const hover = props.hoverColor || undefined;
      const sz    = props.fontSize   || undefined;
      const fw    = props.fontWeight || undefined;
      return (
        <nav className="hidden lg:flex items-center gap-7">
          {menuLinks.map(l => (
            <a key={l.id} href={l.url || '#'}
              className="font-bold text-textLight hover:text-primary transition-colors"
              style={{ color, fontSize: sz, fontWeight: fw }}
              onMouseEnter={hover ? e => (e.currentTarget.style.color = hover) : undefined}
              onMouseLeave={color ? e => (e.currentTarget.style.color = color) : undefined}
            >
              {l.label}
            </a>
          ))}
        </nav>
      );
    }
    case 'search':
      return (
        <button
          onClick={() => openSearch?.()}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-50 transition-colors"
          title="Search"
        >
          <Search className="w-5 h-5 text-gray-600" />
        </button>
      );
    case 'cart': {
      const cartHref = window.themeData?.cartUrl ?? '/cart/';
      return (
        <a href={cartHref} className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-50 transition-colors">
          <ShoppingCart className="w-5 h-5 text-gray-600" />
          {props.showBadge && cartCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center" style={{ fontSize: '9px' }}>{cartCount}</span>
          )}
        </a>
      );
    }
    case 'wishlist': {
      const wishlistHref  = window.themeData?.wishlistUrl ?? '/wishlist/';
      return (
        <a href={wishlistHref} className="relative hidden lg:flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-50 transition-colors" title="Wishlist">
          <Heart className="w-5 h-5 text-gray-600" />
          {props.showBadge && wishlistCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-pink-500 text-white text-xs font-bold rounded-full flex items-center justify-center" style={{ fontSize: '9px' }}>{wishlistCount}</span>
          )}
        </a>
      );
    }
    case 'likes':
      return (
        <button className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors">
          <Heart className="w-4 h-4 text-pink-500" />
          {props.showCount && <span className="text-xs font-bold text-gray-600">{props.count || 0}</span>}
        </button>
      );
    case 'user':
      return <UserMenu />;
    case 'social':
      return (
        <div className="hidden lg:flex gap-2">
          {(props.links ?? []).map(sl => (
            <a key={sl.id} href={sl.url || '#'} target="_blank" rel="noreferrer"
              className="w-8 h-8 rounded-lg text-white text-xs font-bold flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
              style={{ backgroundColor: SOCIAL_COLOR[sl.platform] ?? '#555' }}>
              {SOCIAL_ABBR[sl.platform] ?? '?'}
            </a>
          ))}
        </div>
      );
    case 'button':
      return (
        <a href={props.url || '#'}
          className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
            props.style === 'outline' ? 'border-2 border-primary text-primary hover:bg-primary/5' :
            props.style === 'secondary' ? 'bg-accent1 text-white hover:bg-accent1/90' :
            'bg-primary text-white hover:bg-primary/90'
          }`}>
          {props.label || 'Click Here'}
        </a>
      );
    case 'contact': {
      const isPhone = props.icon === 'phone';
      const href = isPhone ? `tel:${(props.value || '').replace(/[\s()-]/g, '')}` : `mailto:${props.value || ''}`;
      return (
        <a href={href} className="hidden lg:flex items-center gap-1.5 text-sm text-textLight hover:text-primary transition-colors">
          {isPhone
            ? <Phone className="w-3.5 h-3.5" style={{ color: props.iconColor || undefined }} />
            : <Mail className="w-3.5 h-3.5" style={{ color: props.iconColor || undefined }} />}
          <span style={{ color: props.color || undefined }}>{props.value}</span>
        </a>
      );
    }
    case 'text':
      return (
        <span
          className={props.variant === 'heading' ? 'font-black text-base text-textDark' : props.variant === 'small' ? 'text-xs text-textLight' : 'text-sm text-textLight'}
          dangerouslySetInnerHTML={{ __html: props.html || props.text || '' }}
        />
      );
    case 'divider':
      return (
        <div style={{
          width:           props.thickness || '1px',
          height:          props.height    || '20px',
          backgroundColor: props.color     || '#e5e7eb',
          marginLeft:      props.margin    || '4px',
          marginRight:     props.margin    || '4px',
          flexShrink: 0,
        }} />
      );
    case 'spacer':
      return props.flex ? <div className="flex-1" /> : <div style={{ width: props.width || '16px' }} />;
    case 'html':
      return <div dangerouslySetInnerHTML={{ __html: props.html || '' }} />;
    case 'icon': {
      const Ic = ICON_OPTIONS[props.name] || ICON_OPTIONS.Star;
      const sz = props.size || '20px';
      const inner = (
        <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center',
          padding: props.bgColor ? (props.padding || '6px') : '0',
          backgroundColor: props.bgColor || undefined,
          borderRadius: props.bgColor ? (props.bgRounded ? '9999px' : '6px') : undefined,
          flexShrink: 0 }}>
          <Ic style={{ color: props.color || undefined, width: sz, height: sz }} />
        </div>
      );
      return props.url ? <a href={props.url} target="_blank" rel="noreferrer">{inner}</a> : inner;
    }
    default:
      return null;
  }
}

// ── Default settings ──────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2);

export const DEFAULT_HEADER_MODERN = {
  variant: 'modern',
  topBar: {
    enabled:      true,
    email:        'hello@kiddiejoy.com',
    phone:        '+1 (800) 555-1234',
    promoText:    'Free shipping on orders over $50!',
    shippingText: 'Free shipping over $50',
    socials: [
      { id: 's1', platform: 'instagram', url: '#' },
      { id: 's2', platform: 'facebook',  url: '#' },
      { id: 's3', platform: 'tiktok',    url: '#' },
      { id: 's4', platform: 'youtube',   url: '#' },
      { id: 's5', platform: 'pinterest', url: '#' },
    ],
  },
  mainBar: {
    style: { bgColor: '#ffffff', sticky: true, shadow: true, borderBottom: true, height: '80px' },
    columns: [
      { id: 'col-l', flex: 1, align: 'start',  elements: [{ id: 'logo-1', type: 'logo',    props: { logoType: 'text', logoText: 'KiddieJoy', logoUrl: '' } }] },
      { id: 'col-c', flex: 2, align: 'center', elements: [{ id: 'nav-1',  type: 'menu',    props: { links: [{ id: 'l1', label: 'Home', url: '/' }, { id: 'l2', label: 'Shop', url: '/shop/' }, { id: 'l3', label: 'Sale 🔥', url: '/sale/' }] } }] },
      { id: 'col-r', flex: 1, align: 'end',    elements: [{ id: 'src-1', type: 'search',  props: {} }, { id: 'crt-1', type: 'cart', props: { showBadge: true } }, { id: 'usr-1', type: 'user', props: {} }] },
    ]
  },
  customHtml: '',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function HeaderModern({ settings = {}, cartCount = 0 }) {
  const s         = Object.keys(settings).length ? settings : DEFAULT_HEADER_MODERN;
  const top       = s.topBar    ?? DEFAULT_HEADER_MODERN.topBar;
  const main      = s.mainBar   ?? DEFAULT_HEADER_MODERN.mainBar;
  const extraRows = s.extraRows ?? [];
  const style     = main.style  ?? {};
  const cols      = main.columns ?? [];
  const siteUrl   = window.themeData?.siteUrl ?? '/';

  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [searchOpen,     setSearchOpen]     = useState(false);
  const [liveCartCount,  setLiveCartCount]  = useState(cartCount);
  const [liveWishCount,  setLiveWishCount]  = useState(() => window.themeData?.wishlistCount ?? 0);

  useEffect(() => {
    const onCart = (e) => setLiveCartCount(e.detail?.count ?? 0);
    const onWish = (e) => setLiveWishCount(e.detail?.items?.length ?? 0);
    window.addEventListener('anvi:cart-update',     onCart);
    window.addEventListener('anvi:wishlist-update', onWish);
    return () => {
      window.removeEventListener('anvi:cart-update',     onCart);
      window.removeEventListener('anvi:wishlist-update', onWish);
    };
  }, []);

  const alignCls = { start: 'justify-start', center: 'justify-center', end: 'justify-end' };
  const ctx = { cartCount: liveCartCount, wishlistCount: liveWishCount, siteUrl, openSearch: () => setSearchOpen(true) };

  // Collect nav links for mobile menu — prefer WP menu items if menuId is set
  const _navMenuItems = window.themeData?.navMenuItems ?? {};
  const allNavLinks = cols.flatMap(c =>
    c.elements.filter(e => e.type === 'menu').flatMap(e => {
      const id = e.props?.menuId;
      if (id && _navMenuItems[id]) return _navMenuItems[id].filter(i => !i.parent);
      return e.props?.links ?? [];
    })
  );
  const logoEl      = cols.flatMap(c => c.elements.find(e => e.type === 'logo')).find(Boolean);

  return (
    <>
      {/* Announcement bar */}
      <div className="hidden bg-secondary text-textDark text-xs font-black text-center py-2 px-4 tracking-wide">
        🚚 Standard delivery ₹70 &nbsp;|&nbsp; Free shipping on orders above ₹1999
      </div>

      {/* Top bar — sticky so it stays with the main bar */}
      {top.enabled && (
        <div className={style.sticky ? 'sticky top-0 z-50' : ''}>
          <TopBar settings={top} />
        </div>
      )}

      {/* Main bar */}
      <header
        className={`bg-white/95 backdrop-blur-lg${style.borderBottom ? ' border-b border-gray-100' : ''}${style.shadow ? ' shadow-sm' : ''}${style.sticky ? ` sticky ${top.enabled ? 'top-10' : 'top-0'} z-50` : ''}`}
        style={{ backgroundColor: style.bgColor ?? '#ffffff', height: style.height ?? '80px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center h-full gap-3">
            {cols.map((col, i) => {
              const mobileHidden = col.elements.length > 0 && col.elements.every(el => el.type === 'menu');
              return (
              <div
                key={col.id}
                className={`${mobileHidden ? 'hidden lg:flex' : 'flex'} items-center gap-2 ${alignCls[col.align ?? 'start'] ?? ''}`}
                style={{ flex: col.flex ?? 1 }}
              >
                {col.elements.map(el => (
                  <span key={el.id}>{renderElement(el, ctx)}</span>
                ))}
                {/* Hamburger in last column */}
                {i === cols.length - 1 && (
                  <button onClick={() => setMobileOpen(true)} className="lg:hidden text-primary ml-1">
                    <Menu className="w-7 h-7" />
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>
        {s.customHtml && <div dangerouslySetInnerHTML={{ __html: s.customHtml }} />}
      </header>

      {/* Extra rows (secondary nav bars etc.) */}
      {extraRows.map((row) => {
        const rowBg     = row.style?.bgColor     ?? '#f9fafb';
        const rowBorder = row.style?.borderBottom ?? true;
        const rowBdClr  = row.style?.borderColor  ?? '#e5e7eb';
        const rowPad    = row.style?.padding       ?? '8px 0';
        return (
          <div
            key={row.id}
            style={{ backgroundColor: rowBg, borderBottom: rowBorder ? `1px solid ${rowBdClr}` : 'none', padding: rowPad }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4">
              {row.columns.map((col) => (
                <div
                  key={col.id}
                  className="flex items-center gap-2"
                  style={{ flex: col.flex ?? 1, justifyContent: col.align === 'end' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start' }}
                >
                  {col.elements.map((el) => (
                    <span key={el.id}>{renderElement(el, ctx)}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-white overflow-y-auto">
          <div className="p-6 relative">
            <button onClick={() => setMobileOpen(false)} className="absolute top-6 right-6 text-gray-600">
              <X className="w-8 h-8" />
            </button>

            {logoEl && <div className="mb-6">{renderElement(logoEl, ctx)}</div>}

            {/* Mobile search */}
            <button
              onClick={() => { setMobileOpen(false); setSearchOpen(true); }}
              className="w-full flex items-center gap-3 bg-gray-50 rounded-2xl px-5 py-4 border border-gray-200 mb-6 text-left hover:border-primary/30 transition-colors"
            >
              <Search className="w-5 h-5 text-gray-400" />
              <span className="text-base text-gray-400 font-bold">Search for toys, cycles, books…</span>
            </button>

            {/* Nav links */}
            <nav className="space-y-1 mb-8">
              {allNavLinks.map(l => (
                <a key={l.id} href={l.url}
                  className="flex items-center justify-between py-3.5 border-b border-gray-100 text-xl font-black text-textDark hover:text-primary transition-colors group"
                >
                  {l.label}
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-primary transition-colors" />
                </a>
              ))}
            </nav>

            {/* Account section */}
            {(() => {
              const isLoggedIn  = !!window.themeData?.isLoggedIn;
              const displayName = window.themeData?.userDisplayName ?? '';
              const userEmail   = window.themeData?.userEmail ?? '';
              const myAccUrl    = window.themeData?.myAccountUrl ?? `${siteUrl}/my-account/`;
              const logoutUrl   = window.themeData?.logoutUrl ?? `${myAccUrl}?action=logout`;
              const initial     = displayName?.[0]?.toUpperCase() ?? 'U';

              if (!isLoggedIn) {
                return (
                  <div className="border-t border-gray-100 pt-6">
                    <p className="text-xs font-black uppercase tracking-wider text-gray-400 mb-3">Account</p>
                    <a
                      href={myAccUrl}
                      className="flex items-center gap-4 p-4 bg-primary/5 rounded-2xl border border-primary/20 hover:bg-primary/10 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-black text-textDark text-base">Login / Register</p>
                        <p className="text-xs text-gray-400 font-bold mt-0.5">Access your account</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-300 ml-auto" />
                    </a>
                  </div>
                );
              }

              return (
                <div className="border-t border-gray-100 pt-6">
                  {/* Avatar row */}
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-primary/5 to-accent1/5 rounded-2xl mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center text-primary font-black text-lg flex-shrink-0">
                      {initial}
                    </div>
                    <div className="min-w-0">
                      <p className="font-black text-textDark text-base leading-tight truncate">{displayName}</p>
                      {userEmail && <p className="text-xs text-gray-400 mt-0.5 truncate">{userEmail}</p>}
                    </div>
                  </div>

                  {/* Account links */}
                  <div className="space-y-1 mb-4">
                    {ACCOUNT_LINKS.map(({ label, path, icon: Icon }) => (
                      <a
                        key={path}
                        href={`${myAccUrl.replace(/\/$/, '')}/${path}`}
                        className="flex items-center gap-4 px-4 py-3.5 rounded-2xl hover:bg-primary/5 text-base font-bold text-textDark hover:text-primary transition-colors group"
                      >
                        <Icon className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors flex-shrink-0" />
                        {label}
                        <ChevronRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-primary transition-colors" />
                      </a>
                    ))}
                  </div>

                  {/* Logout */}
                  <a
                    href={logoutUrl}
                    className="flex items-center gap-4 px-4 py-3.5 rounded-2xl hover:bg-red-50 text-base font-bold text-red-500 transition-colors"
                  >
                    <LogOut className="w-5 h-5 flex-shrink-0" />
                    Log Out
                  </a>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Search modal */}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
